import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, ShieldCheck, Clock, IndianRupee } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";

const CONSENT = "I agree that OMI Fintech Pvt Ltd and its partner lenders may contact me by call, SMS, WhatsApp or email about my loan request.";
const ATTR_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "ref", "gclid", "fbclid"];

const sessionKey = (k) => {
  let v = sessionStorage.getItem(k);
  if (!v) { v = crypto.randomUUID(); sessionStorage.setItem(k, v); }
  return v;
};

const readAttribution = () => {
  const q = new URLSearchParams(window.location.search);
  const saved = JSON.parse(sessionStorage.getItem("omi-attr") || "{}");
  const fresh = Object.fromEntries(ATTR_KEYS.filter((k) => q.get(k)).map((k) => [k, q.get(k)]));
  const attr = { ...saved, ...fresh, landing_path: window.location.pathname };
  sessionStorage.setItem("omi-attr", JSON.stringify(attr));
  return attr;
};

const F = ({ label, children }) => <div><Label className="text-xs font-semibold text-slate-600">{label}</Label><div className="mt-1">{children}</div></div>;

export function DynamicFields({ fields, values, onChange, creditScore }) {
  return (
    <>
      {fields.map((f) => (
        <F key={f.key} label={`${f.label}${f.required ? " *" : ""}`}>
          {f.type === "select" ? (
            <Select value={values[f.key] || ""} onValueChange={(x) => onChange(f.key, x)}>
              <SelectTrigger data-testid={`public-loan-field-${f.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{(f.options || []).filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input type={f.type === "tel" ? "tel" : f.type} inputMode={["tel", "number"].includes(f.type) ? "numeric" : undefined}
              value={values[f.key] || ""} onChange={(e) => onChange(f.key, e.target.value)} data-testid={`public-loan-field-${f.key}`} />
          )}
        </F>
      ))}
      {creditScore && (
        <F label="Your credit score, if you know it (optional)">
          <Input type="number" inputMode="numeric" min="300" max="900" value={values.credit_score_self || ""} onChange={(e) => onChange("credit_score_self", e.target.value)} data-testid="public-loan-credit-score" />
          <p className="text-[11px] text-slate-500 mt-1">Self-reported. OMI does not run a credit check from this form.</p>
        </F>
      )}
    </>
  );
}

export default function PublicLoanApply() {
  const [opts, setOpts] = useState({ loan_types: [], lenders: [], max_amount: 100000 });
  const [v, setV] = useState({ name: "", mobile: "", pincode: "", amount: "", loan_type: "", lender_id: "", website: "" });
  const [form, setForm] = useState({ fields: [], credit_score_enabled: false });
  const [extra, setExtra] = useState({});
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [attr] = useState(readAttribution);
  useEffect(() => {
    api.get("/public/loan-options").then(({ data }) => setOpts(data)).catch(() => {});
    api.post("/public/loan-event", { event: "form_opened", session_id: sessionKey("omi-loan-session"), attribution: attr }).catch(() => {});
  }, [attr]);
  useEffect(() => {
    api.get("/public/loan-form", { params: { product: v.loan_type || undefined, campaign: attr.utm_campaign || undefined } }).then(({ data }) => setForm(data)).catch(() => {});
  }, [v.loan_type, attr]);
  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const [formInView, setFormInView] = useState(false);
  useEffect(() => {
    const el = document.getElementById("loan-form");
    if (!el || !("IntersectionObserver" in window)) return undefined;
    const ob = new IntersectionObserver(([e]) => setFormInView(e.isIntersecting), { threshold: 0.15 });
    ob.observe(el);
    return () => ob.disconnect();
  }, []);
  const scrollToForm = () => document.getElementById("loan-form")?.scrollIntoView({ behavior: "smooth", block: "start" });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr(""); setBusy(true);
    api.post("/public/loan-event", { event: "form_submitted", session_id: sessionKey("omi-loan-session"), attribution: attr }).catch(() => {});
    try {
      const { data } = await api.post("/public/loan-apply", {
        ...extra, ...v, amount: Number(v.amount), consent, consent_text: CONSENT, attribution: attr,
        session_id: sessionKey("omi-loan-session"), submission_id: sessionKey("omi-loan-submission"),
      });
      sessionStorage.removeItem("omi-loan-submission");
      setDone(data);
      if (data.redirect_url) window.location.assign(data.redirect_url);
    } catch (ex) { setErr(apiError(ex.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center gap-3">
          <img src="/omi-logo.webp" alt="OMI Fintech Pvt Ltd" className="h-12 w-auto" />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-12 grid lg:grid-cols-5 gap-6 sm:gap-10">
        <section className="lg:col-span-2">
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight" data-testid="public-loan-heading">Apply for a loan up to ₹1,00,000</h1>
          <p className="mt-4 text-slate-600">Share a few details and our loan team will call you back with options from our partner banks and NBFCs.</p>
          <ul className="mt-8 space-y-4 text-sm text-slate-700 hidden sm:block">
            <li className="flex gap-3"><Clock className="h-5 w-5 text-blue-600 shrink-0" />Takes under 2 minutes</li>
            <li className="flex gap-3"><IndianRupee className="h-5 w-5 text-blue-600 shrink-0" />No fee to apply with OMI</li>
            <li className="flex gap-3"><ShieldCheck className="h-5 w-5 text-blue-600 shrink-0" />Your details are only shared with lenders for your request</li>
          </ul>
          <p className="mt-8 text-xs text-slate-500">Final approval, amount, interest rate and terms are decided only by the lender after checking eligibility.</p>
        </section>
        <section id="loan-form" className="scroll-mt-4 lg:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
          {form.title && !done && <h2 className="text-lg font-bold mb-4" data-testid="public-loan-form-title">{form.title}</h2>}
          {done ? (
            <div className="text-center py-10" data-testid="public-loan-success">
              <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto" />
              <h2 className="text-lg font-bold mt-4">Thank you! Your request is received.</h2>
              <p className="text-sm text-slate-600 mt-2">{done.redirect_url ? "Taking you to the lender's website to continue…" : "Our loan team will call you shortly to continue your application."}</p>
              {!done.redirect_url && <p className="text-xs text-slate-500 mt-2" data-testid="public-loan-next-step">Keep your PAN and recent bank statement handy for the call.</p>}
              <a href="/status" className="text-blue-600 text-sm font-semibold mt-3 inline-block" data-testid="public-loan-status-link">Check your request status anytime</a>
              {done.redirect_url && <a href={done.redirect_url} className="text-blue-600 text-sm font-semibold mt-3 inline-block" data-testid="public-loan-redirect-link">Continue to lender</a>}
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="public-loan-form">
              <F label="Full name *"><Input value={v.name} onChange={set("name")} data-testid="public-loan-name" /></F>
              <F label="Mobile number *"><Input type="tel" inputMode="numeric" value={v.mobile} onChange={set("mobile")} data-testid="public-loan-mobile" /></F>
              <F label="Loan amount (₹) *"><Input type="number" inputMode="numeric" min="1" max={opts.max_amount} value={v.amount} onChange={set("amount")} data-testid="public-loan-amount" /></F>
              <F label="PIN code *"><Input inputMode="numeric" value={v.pincode} onChange={set("pincode")} maxLength={6} data-testid="public-loan-pincode" /></F>
              <F label="Loan type">
                <Select value={v.loan_type} onValueChange={set("loan_type")}><SelectTrigger data-testid="public-loan-type"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{opts.loan_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </F>
              <DynamicFields fields={form.fields} values={extra} onChange={(k, x) => setExtra((p) => ({ ...p, [k]: x }))} creditScore={form.credit_score_enabled} />
              {opts.lenders.length > 0 && (
                <div className="sm:col-span-2"><F label="Preferred lender (optional)">
                  <Select value={v.lender_id} onValueChange={set("lender_id")}><SelectTrigger data-testid="public-loan-lender"><SelectValue placeholder="Let OMI suggest" /></SelectTrigger>
                    <SelectContent>{opts.lenders.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>
                </F></div>
              )}
              <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={v.website} onChange={set("website")} aria-hidden="true" />
              <label className="sm:col-span-2 flex gap-3 items-start text-xs text-slate-600">
                <Checkbox checked={consent} onCheckedChange={(c) => setConsent(!!c)} data-testid="public-loan-consent" className="mt-0.5" />{CONSENT}
              </label>
              {err && <div className="sm:col-span-2 text-sm text-rose-600" data-testid="public-loan-error">{err}</div>}
              <Button type="submit" disabled={busy} className="sm:col-span-2 h-11" data-testid="public-loan-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit loan request"}
              </Button>
            </form>
          )}
        </section>
      </main>
      <footer className="text-center text-xs text-slate-500 pb-24 lg:pb-8">© OMI Fintech Pvt Ltd</footer>
      {!done && !formInView && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 p-3 bg-white/95 backdrop-blur border-t border-slate-200">
          <Button className="w-full h-12 text-base font-semibold" onClick={scrollToForm} data-testid="public-loan-sticky-apply">Apply now</Button>
        </div>
      )}
      <SiteFooter />
    </div>
  );
}
